import type { BaseQueryApi, BaseQueryFn, FetchArgs, FetchBaseQueryError } from '@reduxjs/toolkit/query';

import type { RootState } from '@app/store/store';
import { authExpired } from '@features/authentication/store/authentication.slice';
import { refreshAccessToken } from '@shared/utils/access-token-refresh';
import { RefreshStatus } from '@shared/utils/access-token-refresh.types';
import { baseQuery } from '@shared/utils/base-query.utils';

type BaseQueryArgs = string | FetchArgs;

// We strictly forbid the interceptor from catching 401 errors on these specific routes.
// If `/refresh` itself returns a 401, and we try to catch it and trigger ANOTHER `/refresh`,
// we will create an infinite loop that crashes the browser.
const AUTH_ENDPOINT_PATHS = ['/api/v1/auth/refresh', '/api/v1/auth/login', '/api/v1/auth/logout'];

const isAuthEndpoint = (args: BaseQueryArgs): boolean => {
  const url = typeof args === 'string' ? args : args.url;
  return AUTH_ENDPOINT_PATHS.some((path) => url.includes(path));
};

/**
 * THE API INTERCEPTOR (Re-Auth Wrapper)
 * Wraps every API call made by the application.
 * If a request fails with a 401 (Missing/Expired Access Token), this interceptor puts
 * the original request on pause, silently asks the access-token-refresh coordinator for a new token,
 * and then retries the original request.
 */
export const fetchBaseQueryWithTokenCheck: BaseQueryFn<BaseQueryArgs, unknown, FetchBaseQueryError> = async (
  args,
  api: BaseQueryApi,
  extraOptions,
) => {
  // Step 1: Fire the original request.
  const result = await baseQuery(args, api, extraOptions);

  // If it's successful, or it's an error OTHER than 401, or it's an Auth endpoint... just return it.
  // Note: A 403 (Forbidden) means the user lacks permissions, NOT that the token is expired.
  if (result.error?.status !== 401 || isAuthEndpoint(args)) {
    return result;
  }

  // Step 2: It's a 401. We need a new token.
  // We capture the CURRENT token from memory before asking for a refresh.
  // This allows the access-token-refresh coordinator to know if another tab has ALREADY refreshed the token
  // while we were waiting, preventing duplicate refresh requests.
  const staleToken = (api.getState() as RootState).authentication.token;
  const outcome = await refreshAccessToken(api, staleToken);

  // Step 3: Handle the refresh outcome.
  if (outcome.status === RefreshStatus.REFRESHED) {
    // Success! Retry the original request (it will automatically use the new token from state).
    return baseQuery(args, api, extraOptions);
  }

  if (outcome.status === RefreshStatus.FAILED || outcome.status === RefreshStatus.UNREACHABLE) {
    // Failure! The refresh cookie is dead, OR the server is unreachable.
    // Unlike the Bootstrap process, if we hit a 401 at runtime and the network drops,
    // we default to safety and kick the user out by firing the "flare gun" (authExpired).
    api.dispatch(authExpired());
  }

  // If status is CANCELLED, it means a logout is already in progress.
  // We stay quiet and do nothing.

  return result;
};
